import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, VideoPresets } from 'livekit-client';
import { livekit_url } from '../api/Config';
import { playJoinSound, playLeaveSound } from '../utils/soundEffects';
import { getMicDefaultOn } from '../utils/voiceSettings';

// Remembers what volume the user set a given participant to, across rooms
// and app restarts - identity is a stable per-user id (see
// Backend/utils/livekitToken.js's createVoiceToken), so "quieter for this
// one friend" sticks no matter which room they're both in next. The music
// bot's identity is room-scoped (`bot:music:${roomId}`) rather than
// user-scoped, so its entry only carries over on a *recurring* room (the
// common case - most rooms are someone's stable personal/group room) and
// simply won't match in a genuinely different one, which is fine.
const PARTICIPANT_VOLUME_STORAGE_KEY = 'participantVolumes';

function loadStoredVolumes() {
    try {
        return JSON.parse(localStorage.getItem(PARTICIPANT_VOLUME_STORAGE_KEY)) || {};
    } catch {
        return {};
    }
}

function persistVolume(identity, volume) {
    const stored = loadStoredVolumes();
    stored[identity] = volume;
    localStorage.setItem(PARTICIPANT_VOLUME_STORAGE_KEY, JSON.stringify(stored));
}

// No stored preference yet: the music bot defaults to 10% (voice chat, not a
// concert - it shouldn't drown people out the moment it joins), everyone
// else keeps the unchanged 100%. A stored preference always wins over this,
// regardless of which value it is - this default is only ever the very
// first volume a given identity gets before the user ever touches its
// slider.
function defaultVolumeFor(identity) {
    const stored = loadStoredVolumes();
    if (identity in stored) return stored[identity];
    return identity.startsWith('bot:') ? 0.1 : 1;
}

// Connects to the LiveKit room while `active` is true, using whatever the
// latest voiceToken is at the moment the connection is (re)established.
// `active` is a plain boolean derived from "am I currently in an app room" -
// it must NOT flip on every background token refresh, or a routine refresh
// would tear down and rebuild the WebRTC session mid-call. The token itself
// is read from a ref so a refresh updates what a *future* reconnect would
// use without touching the live connection. `roomId` is what actually keys
// the reconnect below - see the effect's dependency array comment.
export function useVoiceConnection(voiceToken, roomId, active, options = {}) {
    // Meetings (screens/Meeting.js) reuse this whole WebRTC implementation but
    // decide mic/camera state in their own pre-join lobby instead of from the
    // app-wide Settings preference, and need the Room object itself to reach
    // LiveKit's data channel (useMeetingSignals) - which group rooms never do,
    // since they have an authenticated socket for the same job. Both are
    // opt-in: with no options passed, this behaves exactly as it always has.
    const { micDefaultOn: micDefaultOnOverride = null, cameraDefaultOn = false, onRoom = null, spatialAudio = false } = options;
    const optionsRef = useRef({ micDefaultOnOverride, cameraDefaultOn, onRoom });
    optionsRef.current = { micDefaultOnOverride, cameraDefaultOn, onRoom };

    const roomRef = useRef(null);
    const tokenRef = useRef(voiceToken);
    tokenRef.current = voiceToken;

    const [connected, setConnected] = useState(false);
    const [participants, setParticipants] = useState([]); // [{identity, name, speaking, muted, cameraEnabled, isLocal, volume}]
    const [muted, setMuted] = useState(() => !(micDefaultOnOverride === null ? getMicDefaultOn() : micDefaultOnOverride)); // mic starts off unless the user opted into on-by-default in Settings, or a caller overrode it
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [error, setError] = useState(null);

    // identity -> live VideoTrack (local or remote). State, not a ref like
    // audioTracksRef below - the grid UI needs to re-render when a track
    // appears/disappears, whereas audio never renders anything itself (it's
    // attached to a hidden <audio> element directly, see handleTrackSubscribed).
    const [videoTracks, setVideoTracks] = useState({});
    // Positive polarity on purpose (unlike `muted`) - "camera unmuted" reads
    // badly, and this mirrors the mic-starts-off convention: camera also
    // starts off.
    const [cameraOn, setCameraOn] = useState(false);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState(null);

    // identity -> live screen-share VideoTrack (local or remote) - kept
    // entirely separate from videoTracks above. A participant can have BOTH
    // a camera and a screen-share track live at once (different
    // Track.Source), and videoTracks is keyed only by identity - folding
    // screen-share into it would silently clobber whichever track published
    // second. identity -> screen-share AudioTrack mirrors it (present only
    // when the sharer included system/tab audio - see toggleScreenShare).
    const [screenShareTracks, setScreenShareTracks] = useState({});
    const [screenShareAudioTracks, setScreenShareAudioTracks] = useState({});
    const [screenShareOn, setScreenShareOn] = useState(false);

    // identity -> volume (0..3, 1 = 100%/unchanged) for THIS session - seeded
    // lazily from localStorage/defaultVolumeFor the first time each identity
    // is seen (see getOrInitVolume below), so it doesn't need re-reading on
    // every access. Read by refreshParticipants so a slider's position
    // survives unrelated re-renders (someone else muting, a speaking-state
    // change, ...), and re-applied in handleTrackSubscribed so it survives a
    // participant's track being replaced (e.g. device switch) without the
    // listener having to re-set it.
    const volumesRef = useRef(new Map());
    // identity -> Set<live audio Track>, so setParticipantVolume can push a
    // change immediately instead of waiting for the next subscription. A
    // Set, not a single Track: once screen-share audio exists, one identity
    // can have both a microphone track and a screen-share-audio track live
    // at the same time, and volume should apply to both (turning someone
    // down should turn their shared tab's audio down too, not leave it at
    // full volume behind their now-quieter mic).
    const audioTracksRef = useRef(new Map());

    // Spatial audio (meetings only). identity -> StereoPannerNode, inserted
    // into each remote track's own web-audio chain via LiveKit's
    // setWebAudioPlugins. Those nodes have to come from the SAME AudioContext
    // the SDK is mixing in, which is why the context below is ours and handed
    // to the Room rather than left for it to create - webAudioMix accepts one
    // precisely so a caller can reach into the graph like this.
    const pannersRef = useRef(new Map());
    const audioContextRef = useRef(null);
    // Read inside the connect effect without making it a dependency: turning
    // spatial audio on or off should never tear down a live call.
    const spatialAudioRef = useRef(spatialAudio);
    spatialAudioRef.current = spatialAudio;

    useEffect(() => {
        if (!active || !tokenRef.current) return undefined;
        let cancelled = false;
        // webAudioMix routes remote audio through a Web Audio GainNode instead
        // of just setting <audio>.volume - required for boosting a
        // participant above 100%, since the native element property is
        // clamped to [0,1] by the browser regardless of what you assign it.
        // adaptiveStream/dynacast keep a small self-hosted node's bandwidth in
        // check automatically: adaptiveStream stops subscribing to a remote
        // video whose <video> element is hidden/zero-sized (see VideoTile in
        // VideoGridOverlay, which relies on this), dynacast pauses simulcast
        // layers nobody's currently receiving. Capture/simulcast capped at
        // 540p/360p/180p - plenty for a room-sized grid, not a studio call.
        // One context per connection, closed in the teardown below. Created
        // here rather than module-scope so a second meeting in the same tab
        // never inherits a context that was already closed.
        if (spatialAudioRef.current && !audioContextRef.current) {
            const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
            if (AudioContextCtor) audioContextRef.current = new AudioContextCtor();
        }

        const room = new Room({
            webAudioMix: audioContextRef.current ? { audioContext: audioContextRef.current } : true,
            adaptiveStream: true,
            dynacast: true,
            videoCaptureDefaults: { resolution: VideoPresets.h540.resolution },
            publishDefaults: { simulcast: true, videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] }
        });
        roomRef.current = room;

        // First time this identity is seen this session: seed volumesRef from
        // localStorage/the bot-vs-human default, so both this initial read
        // and every later one agree on the same value instead of each
        // re-deriving (and re-parsing localStorage) independently.
        const getOrInitVolume = (identity) => {
            if (!volumesRef.current.has(identity)) volumesRef.current.set(identity, defaultVolumeFor(identity));
            return volumesRef.current.get(identity);
        };

        const refreshParticipants = () => {
            if (cancelled) return;
            const all = [room.localParticipant, ...Array.from(room.remoteParticipants.values())];
            setParticipants(all.map((p) => ({
                identity: p.identity,
                name: p.name,
                // Only meetings set this (createMeetingToken puts the signed-in
                // user's avatar in it so guests can render a real picture
                // without being allowed to call the user API). Always present
                // on the object, just undefined for group rooms.
                metadata: p.metadata,
                speaking: p.isSpeaking,
                muted: !p.isMicrophoneEnabled,
                cameraEnabled: p.isCameraEnabled,
                screenSharing: p.isScreenShareEnabled,
                isLocal: p === room.localParticipant,
                volume: p === room.localParticipant ? 1 : getOrInitVolume(p.identity)
            })));
        };

        // ParticipantConnected/Disconnected only ever fire for *remote*
        // participants (LiveKit's own local participant doesn't generate
        // these against itself) - so this naturally never plays a sound for
        // your own join/leave, only for others already in or joining the
        // call, matching how e.g. Discord does it. The music bot is also a
        // real participant (see botIdentityFor in Backend/utils/livekitToken.js)
        // but connecting to play a track isn't a "someone joined" moment, so
        // it's excluded by its 'bot:' identity prefix.
        room.on(RoomEvent.ParticipantConnected, (participant) => {
            if (!participant.identity.startsWith('bot:')) playJoinSound();
            refreshParticipants();
        });
        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            if (!participant.identity.startsWith('bot:')) playLeaveSound();
            refreshParticipants();
        });
        room.on(RoomEvent.ActiveSpeakersChanged, refreshParticipants);
        room.on(RoomEvent.TrackMuted, refreshParticipants);
        room.on(RoomEvent.TrackUnmuted, refreshParticipants);

        // TrackSubscribed/Unsubscribed (below) only ever fire for *remote*
        // tracks - the local participant's own camera has to be picked up
        // here instead, or the self-preview tile would just never appear.
        // setCameraEnabled(false) unpublishes (not just mutes) the camera by
        // default, which is why this is Published/Unpublished and not
        // Muted/Unmuted - the OS camera light actually goes off.
        room.on(RoomEvent.LocalTrackPublished, (publication) => {
            if (publication.kind !== Track.Kind.Video || !publication.track) return;
            const identity = room.localParticipant.identity;
            const setter = publication.source === Track.Source.ScreenShare ? setScreenShareTracks : setVideoTracks;
            setter((prev) => ({ ...prev, [identity]: publication.track }));
            refreshParticipants();
        });
        room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
            if (publication.kind !== Track.Kind.Video) return;
            const identity = room.localParticipant.identity;
            const setter = publication.source === Track.Source.ScreenShare ? setScreenShareTracks : setVideoTracks;
            setter((prev) => {
                if (prev[identity] !== publication.track) return prev;
                const next = { ...prev };
                delete next[identity];
                return next;
            });
            refreshParticipants();
        });

        room.on(RoomEvent.Disconnected, () => { if (!cancelled) setConnected(false); });

        // The SDK negotiates and receives remote audio on its own, but never
        // plays it anywhere by itself - without this, WebRTC stats/bytes
        // look completely healthy (ICE connected, RTP flowing) while nobody
        // actually hears anything, since the decoded audio has nowhere to
        // go. attach() returns a real <audio> element that has to be added
        // to the DOM for (some) browsers to actually play it; kept hidden
        // since this is audio-only, no visual track to show.
        const attachedElements = new Map(); // track.sid -> HTMLMediaElement, audio only
        const handleTrackSubscribed = (track, publication, participant) => {
            if (track.kind === Track.Kind.Audio) {
                // Screen-share audio is rendered inside ScreenShareOverlay
                // itself (attached directly to its <video> element, alongside
                // the video track it belongs with) rather than through this
                // hidden-<audio>-element path - that overlay only exists for
                // the one active sharer, so routing it there instead of here
                // avoids double-playing it.
                if (publication.source === Track.Source.ScreenShareAudio) {
                    setScreenShareAudioTracks((prev) => ({ ...prev, [participant.identity]: track }));
                    return;
                }
                const el = track.attach();
                el.style.display = 'none';
                document.body.appendChild(el);
                attachedElements.set(track.sid, el);
                if (!audioTracksRef.current.has(participant.identity)) audioTracksRef.current.set(participant.identity, new Set());
                audioTracksRef.current.get(participant.identity).add(track);
                track.setVolume(getOrInitVolume(participant.identity));

                // Insert this speaker's panner into their own chain. Only
                // microphones: a shared screen's audio is content, not a
                // person in the grid, and panning it would just make it
                // lopsided. (It never reaches here anyway - screen-share
                // audio returns above - but the source check keeps that
                // intent explicit rather than incidental.)
                const context = audioContextRef.current;
                if (context && publication.source === Track.Source.Microphone && track.setWebAudioPlugins) {
                    let panner = pannersRef.current.get(participant.identity);
                    if (!panner) {
                        panner = context.createStereoPanner();
                        pannersRef.current.set(participant.identity, panner);
                    }
                    // Re-applied on every subscribe, not just the first: a
                    // device switch republishes the track, and the new one
                    // starts with an empty plugin chain.
                    track.setWebAudioPlugins([panner]);
                }
                return;
            }
            if (track.kind === Track.Kind.Video) {
                // No attach() here - unlike audio, a video track needs a
                // real, sized, on-screen <video> element (for adaptiveStream
                // to even keep the subscription alive), which only the tile
                // component that's actually rendering it can provide. This
                // hook just hands the track object over; VideoTile/
                // ScreenShareOverlay own attach()/detach() against their own
                // element.
                const setter = publication.source === Track.Source.ScreenShare ? setScreenShareTracks : setVideoTracks;
                setter((prev) => ({ ...prev, [participant.identity]: track }));
                refreshParticipants();
            }
        };
        const handleTrackUnsubscribed = (track, publication, participant) => {
            if (track.kind === Track.Kind.Audio) {
                if (publication.source === Track.Source.ScreenShareAudio) {
                    setScreenShareAudioTracks((prev) => {
                        if (prev[participant.identity] !== track) return prev;
                        const next = { ...prev };
                        delete next[participant.identity];
                        return next;
                    });
                    return;
                }
                track.detach().forEach((el) => el.remove());
                attachedElements.delete(track.sid);
                const set = audioTracksRef.current.get(participant.identity);
                if (set) {
                    set.delete(track);
                    if (set.size === 0) audioTracksRef.current.delete(participant.identity);
                }
                return;
            }
            if (track.kind === Track.Kind.Video) {
                const setter = publication.source === Track.Source.ScreenShare ? setScreenShareTracks : setVideoTracks;
                setter((prev) => {
                    if (prev[participant.identity] !== track) return prev;
                    const next = { ...prev };
                    delete next[participant.identity];
                    return next;
                });
                refreshParticipants();
            }
        };
        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);

        // Best-effort and non-blocking: subscribing to others' audio needs no
        // mic access at all, so a denial here shouldn't stop the connection -
        // it just means the later setMicrophoneEnabled(true) won't work.
        if (window.cyclone && window.cyclone.requestMicPermission) window.cyclone.requestMicPermission();

        room.connect(livekit_url, tokenRef.current)
            .then(async () => {
                if (cancelled) return;
                setConnected(true);
                refreshParticipants();
                // Whether the mic publishes enabled right away is a user
                // preference (Settings > default off unless turned on there) -
                // read fresh on each connect, not just once at hook init, so
                // flipping it in Settings applies from the next room join
                // without needing an app restart.
                const { micDefaultOnOverride: micOverride, cameraDefaultOn: camOnJoin, onRoom: onRoomReady } = optionsRef.current;
                const micDefaultOn = micOverride === null ? getMicDefaultOn() : micOverride;
                await room.localParticipant.setMicrophoneEnabled(micDefaultOn);
                setMuted(!micDefaultOn);
                // Meeting lobbies let people turn their camera on *before*
                // joining, so honour that here rather than making them click
                // the button again the moment they land in the call. Failures
                // are swallowed on purpose - the camera button is right there,
                // and a denied permission shouldn't abort an otherwise
                // working join.
                if (camOnJoin) {
                    try {
                        await room.localParticipant.setCameraEnabled(true);
                        if (!cancelled) setCameraOn(true);
                    } catch (cameraError) { /* leave the camera off; the user can retry from the control bar */ }
                }
                // Handed over only after connect succeeds, so a consumer can
                // assume the Room is live (useMeetingSignals publishes to the
                // data channel the moment it gets one).
                if (onRoomReady && !cancelled) onRoomReady(room);
                // Covers browsers that still block autoplay despite this
                // being inside a user-gesture-initiated flow (joining the
                // room) - a no-op if playback is already allowed.
                room.startAudio().catch(() => {});

                const mediaDevices = await navigator.mediaDevices.enumerateDevices();
                if (!cancelled) {
                    setDevices(mediaDevices.filter((d) => d.kind === 'audioinput'));
                    // Labels are blank here until camera permission is
                    // actually granted (enumerateDevices() doesn't request
                    // it) - re-populated for real once toggleCamera succeeds.
                    setVideoDevices(mediaDevices.filter((d) => d.kind === 'videoinput'));
                }
            })
            .catch((err) => { if (!cancelled) setError(err.message || 'Could not connect to voice.'); });

        return () => {
            cancelled = true;
            // Before disconnect, so a consumer holding the Room (see onRoom
            // above) drops it rather than publishing into a closing session.
            if (optionsRef.current.onRoom) optionsRef.current.onRoom(null);
            room.disconnect();
            roomRef.current = null;
            setConnected(false);
            setParticipants([]);
            setVideoTracks({});
            setCameraOn(false);
            setScreenShareTracks({});
            setScreenShareAudioTracks({});
            setScreenShareOn(false);
            // room.disconnect() doesn't reliably fire trackUnsubscribed for
            // a teardown-in-progress room, so clean up whatever's left here
            // too instead of leaking <audio> elements into the page.
            attachedElements.forEach((el) => el.remove());
            attachedElements.clear();
            audioTracksRef.current.clear();
            volumesRef.current.clear();
            pannersRef.current.clear();
            // Browsers cap how many AudioContexts a page may hold, so leaving
            // one behind per joined meeting eventually stops audio working at
            // all. Nulled first so a reconnect builds a fresh one rather than
            // reusing a closing handle.
            const context = audioContextRef.current;
            audioContextRef.current = null;
            if (context) context.close().catch(() => {});
        };
        // Re-run when a token becomes available after starting out null (the
        // room:resynced reconnect path issues one asynchronously via
        // refreshVoiceToken), OR when roomId itself changes (moving straight
        // from one room to another - e.g. a knock getting accepted while
        // already sitting in a different room - jumps `currentRoomId` from
        // A to B without `active` ever passing through false, so without
        // roomId here this would keep pumping frames at the OLD LiveKit
        // room and never even attempt the new one). Deliberately NOT on
        // every subsequent background refresh of the same room, which is
        // why this tracks token *presence* rather than its value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, roomId, Boolean(voiceToken)]);

    // Backstop for the rare case electron-starter.js's own handler ends up
    // denying a screen-share request itself (see its macOS branch) instead
    // of toggleScreenShare's proactive status pre-check catching it first -
    // a race against a mid-flow permission change, or the system picker
    // being unavailable for some environment-specific reason. Independent of
    // `active`/room lifecycle since screen permission isn't room-scoped.
    useEffect(() => {
        if (!window.cyclone || !window.cyclone.onScreenPermissionDenied) return undefined;
        return window.cyclone.onScreenPermissionDenied(() => {
            setScreenShareOn(false);
            setError('SCREEN_PERMISSION_DENIED');
        });
    }, []);

    const toggleMute = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        // Belt-and-suspenders: this click is a guaranteed user gesture, so
        // retry starting audio here too in case the browser blocked
        // autoplay right after connecting (see the connect effect above).
        if (!room.canPlaybackAudio) room.startAudio().catch(() => {});
        const next = !muted;
        try {
            await room.localParticipant.setMicrophoneEnabled(!next);
            setMuted(next);
            setError(null);
        } catch (err) {
            // Denied/missing mic - state must stay in sync with reality (still
            // muted) instead of the button silently doing nothing.
            setError(err.message || 'Could not access the microphone.');
        }
    }, [muted]);

    const selectDevice = useCallback(async (deviceId) => {
        const room = roomRef.current;
        if (!room) return;
        await room.switchActiveDevice('audioinput', deviceId);
        setSelectedDeviceId(deviceId);
    }, []);

    const toggleCamera = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        const next = !cameraOn;
        try {
            // Unlike mic permission (requested eagerly at connect time),
            // camera permission is only ever requested here, on the first
            // actual enable - someone who joins just to listen should never
            // see an OS camera prompt or camera-in-use light.
            if (next && window.cyclone && window.cyclone.requestCameraPermission) {
                await window.cyclone.requestCameraPermission();
            }
            await room.localParticipant.setCameraEnabled(next);
            setCameraOn(next);
            setError(null);
            if (next) {
                // Device labels are blank until permission is granted - now
                // that it (probably) is, re-enumerate to get real ones.
                const mediaDevices = await navigator.mediaDevices.enumerateDevices();
                setVideoDevices(mediaDevices.filter((d) => d.kind === 'videoinput'));
            }
        } catch (err) {
            // Denied/missing camera - state must stay in sync with reality
            // (still off) instead of the button silently doing nothing.
            setError(err.message || 'Could not access the camera.');
        }
    }, [cameraOn]);

    // Unlike toggleCamera, macOS screen-recording permission can't be
    // requested programmatically at all (systemPreferences.askForMediaAccess
    // only covers 'microphone'/'camera' - see electron-starter.js) - only
    // read and, if denied, point the user at System Settings instead of
    // letting setScreenShareEnabled() fail with an opaque rejection.
    const toggleScreenShare = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        const next = !screenShareOn;
        try {
            if (next) {
                if (window.cyclone && window.cyclone.getScreenPermissionStatus) {
                    const status = await window.cyclone.getScreenPermissionStatus();
                    if (status === 'denied' || status === 'restricted') {
                        setError('SCREEN_PERMISSION_DENIED');
                        return;
                    }
                }
                // audio:true makes LiveKit's getDisplayMedia() call ask for
                // audio too - Electron's setDisplayMediaRequestHandler (see
                // electron-starter.js) decides per-platform whether that
                // actually comes back as real system audio (Windows: yes,
                // via loopback; macOS: only through the native system
                // picker; Linux: video-only) or is simply absent from the
                // stream, in which case LiveKit just never publishes a
                // screen_share_audio track - no special-casing needed here.
                await room.localParticipant.setScreenShareEnabled(true, { audio: true });
            } else {
                await room.localParticipant.setScreenShareEnabled(false);
            }
            setScreenShareOn(next);
            setError(null);
        } catch (err) {
            // User cancelled the picker (getDisplayMedia rejects with
            // NotAllowedError, same as Electron's handler answering with an
            // empty callback({})) - a deliberate no-op, not a failure worth
            // surfacing as an error banner.
            if (err && err.name !== 'NotAllowedError') setError(err.message || 'Could not start screen sharing.');
            setScreenShareOn(false);
        }
    }, [screenShareOn]);

    const selectVideoDevice = useCallback(async (deviceId) => {
        const room = roomRef.current;
        if (!room) return;
        await room.switchActiveDevice('videoinput', deviceId);
        setSelectedVideoDeviceId(deviceId);
    }, []);

    // volume: 0..3 (0% - 300%). Only meaningful for remote participants - a
    // local track has no "how loud do I sound to myself" control. Persisted
    // immediately (not just kept in volumesRef) so it's there the next time
    // this identity shows up, in this room or a different one.
    // pan: -1 (hard left) .. 1 (hard right). Called from the stage, which is
    // the only thing that knows where a tile actually sits on screen. A no-op
    // when spatial audio is off or the speaker has no live track yet - their
    // panner is created on subscribe, and the next layout pass will set it.
    const setParticipantPan = useCallback((identity, pan) => {
        const panner = pannersRef.current.get(identity);
        const context = audioContextRef.current;
        if (!panner || !context) return;
        // Ramped rather than assigned: a step change in pan is audible as a
        // click, and tiles move whenever anyone joins or leaves.
        panner.pan.setTargetAtTime(Math.max(-1, Math.min(1, pan)), context.currentTime, 0.08);
    }, []);

    const setParticipantVolume = useCallback((identity, volume) => {
        volumesRef.current.set(identity, volume);
        persistVolume(identity, volume);
        // Applies to every audio track this identity currently has live
        // (microphone + screen-share audio, if both are present) - turning
        // someone down should turn all of them down together, see
        // audioTracksRef's declaration above.
        const tracks = audioTracksRef.current.get(identity);
        if (tracks) tracks.forEach((track) => track.setVolume(volume));
        setParticipants((prev) => prev.map((p) => (p.identity === identity ? { ...p, volume } : p)));
    }, []);

    return {
        connected, participants, muted, toggleMute, devices, selectedDeviceId, selectDevice, setParticipantVolume, setParticipantPan, error,
        cameraOn, toggleCamera, videoTracks, videoDevices, selectedVideoDeviceId, selectVideoDevice,
        screenShareOn, toggleScreenShare, screenShareTracks, screenShareAudioTracks
    };
}
